/**
 * Location Service
 * 
 * Unified frontend service for location and search operations.
 * Consolidates Firestore-first search and Google Places API utilities.
 */

import { httpsCallable } from "firebase/functions";
import { doc, getDoc, collection, query, where, documentId, getDocs } from "firebase/firestore";
import { functions, db } from "../firebase";
import type { Gym, LocationSelection } from "../types";
import { safeErrorMessage } from "../utils/sanitize";
import { LAUNCHED_STATES, canonicaliseSuburbName, stateSlugToDisplay } from "../lib/stateCore.mjs";
import type { StateSlug } from "../lib/stateCore.mjs";
import { mergeGymNameResults } from '../lib/gymSearchRanking';
/**
 * Search result from display
 */
export interface GymDisplaySearchResult {
    gyms: Gym[];
    center: { lat: number; lng: number } | null;
    resolved?: string;
    source?: string;
    activeVersion?: string;
}

/**
 * Put every gym the backend returns into register spelling (audit H1/M1).
 *
 * The SSG canonicalises `suburb` where records enter the build, so a
 * prerendered page reads "St Marys". The browser fetches the same gyms
 * straight from the Cloud Function, which returns the raw stored string —
 * without this the hydrated page would repaint the very cards Google was
 * shown as "Saint Marys", re-creating the prerender-vs-runtime split this
 * audit exists to remove (cf. #4, #13). One map at the boundary, so every
 * consumer — cards, profile, JSON-LD, the directory pill — inherits it.
 */
const canonicaliseGymSuburbs = <T extends { suburb?: string }>(gyms: T[] | undefined): T[] =>
    (gyms || []).map(g => (g.suburb ? { ...g, suburb: canonicaliseSuburbName(g.suburb) } : g));

/**
 * Search gyms by location (suburb or postcode)
 * Uses searchGymsFromDisplay Cloud Function
 */
export const searchGymsByLocation = async (
    location: string,
    radius: number = 10,
    postcodeOnly: boolean = true,
    // UI-01 hardening: `state` is REQUIRED, not defaulted. Every silent
    // NSW-fallback in this file was invisible until a second state launched and
    // its pages searched the wrong corpus; omitting the state must now fail the
    // build, so state-less surfaces have to write DEFAULT_STATE deliberately.
    options: { entryId?: string | null | undefined; state: StateSlug }
): Promise<GymDisplaySearchResult> => {
    const searchFn = httpsCallable<
        {
            location: string;
            radius: number;
            postcodeOnly: boolean;
            entryId?: string | null | undefined;
            // Required in the wire type too, so the payload cannot drift from the
            // wrapper contract (review NIT: optional here would let a refactor
            // drop the field silently while the wrapper still compiled).
            state: StateSlug;
        },
        GymDisplaySearchResult
    >(functions, "searchGymsFromDisplay");

    try {
        const result = await searchFn({
            location,
            radius,
            postcodeOnly,
            entryId: options.entryId,
            // R-02: absent state defaults to NSW server-side (P2-A rule), which on a
            // VIC page silently searched the NSW corpus. Callers pass the page's state
            // (required — see the options type above).
            state: options.state
        });
        return { ...result.data, gyms: canonicaliseGymSuburbs(result.data.gyms) };
    } catch (error) {
        console.error("[locationService] searchGymsByLocation error:", safeErrorMessage(error));
        throw error;
    }
};

/**
 * Search gyms by name (prefix match)
 * Uses searchGymsByNameFromDisplay Cloud Function
 */
// UI-01 hardening: `state` REQUIRED — see searchGymsByLocation's options note.
/**
 * ⭐ ITEM 3 (operator-ruled 2026-08-17): search EVERY launched state at once, for the
 * GLOBAL header search bar only.
 *
 * ⚠️ WHY THIS IS A CLIENT-SIDE FAN-OUT AND NOT A BACKEND CHANGE — the scoping deliverable
 * `docs/state-expansion/vic/item3-universal-search-scoping-2026-08-17.md` has the full
 * argument, but in one line: gyms live in PER-STATE Firestore subcollections, so a
 * cross-state search is N queries either way, and **the callable already accepts a state**.
 * Fanning out here needs no `functions/` change, no `deploy:functions`, and no second
 * lane's gate. The cost is one extra invocation per search and the mirrored ranking in
 * `lib/gymSearchRanking.ts`.
 *
 * ⚠️ SCOPE — this is NOT for the directory's own gym-name filter. `useDirectorySearch`
 * deliberately keeps calling `searchGymsByName` with one state: a NSW directory listing
 * Victorian gyms among its results would read as a bug, not a feature (operator-ruled).
 *
 * ⚠️ PARTIAL FAILURE IS TOLERATED BY DESIGN. `allSettled`, not `all`: if one state's
 * callable fails, the visitor sees the other state's matches rather than an empty box.
 * A silent partial is the better failure here — the alternative is losing every result
 * because one region had a bad minute.
 *
 * ⭐ RESULT LABELS ARE SAFE, MEASURED NOT ASSUMED. `searchHandlers.ts:86` falls back to the
 * DEFAULT state display when a document has no `state` field, which in a merged list would
 * label a Victorian gym "NSW". Measured read-only against prod on 2026-08-17 with count()
 * aggregations: **0 of 2,750 display documents lack the field** (nsw 1,493/1,493, vic
 * 1,257/1,257). So the fallback never fires and no client-side re-stamping is needed.
 * **It remains a latent trap** — if that ever stops being true, this is where it surfaces.
 */
export const searchGymsByNameAllStates = async (query: string): Promise<Gym[]> => {
    if (!query || query.trim().length < 3) {
        return [];
    }

    const settled = await Promise.allSettled(
        (LAUNCHED_STATES as readonly StateSlug[]).map((state) => searchGymsByName(query, state)),
    );

    const perState = settled.map((r) => (r.status === 'fulfilled' ? r.value : []));
    const failures = settled.filter((r) => r.status === 'rejected').length;
    if (failures > 0) {
        console.warn(`[locationService] cross-state name search: ${failures} of ${settled.length} state queries failed; showing partial results`);
    }

    return mergeGymNameResults(perState, query);
};

export const searchGymsByName = async (query: string, state: StateSlug): Promise<Gym[]> => {
    if (!query || query.trim().length < 3) {
        return [];
    }

    const searchFn = httpsCallable<{ query: string; state: StateSlug }, { gyms: Gym[] }>(
        functions,
        "searchGymsByNameFromDisplay"
    );

    try {
        // R-02: state omitted → NSW server-side; callers pass the page's state.
        const result = await searchFn({ query: query.trim(), state });
        return canonicaliseGymSuburbs(result.data.gyms);
    } catch (error) {
        console.error("[locationService] searchGymsByName error:", safeErrorMessage(error));
        throw error;
    }
};

/**
 * Fetch single gym by ID from the display collection
 */
/**
 * Fetch a gym from the display collection by its primary key.
 * Now that the display collection uses stableId as the document ID,
 * this is a high-performance O(1) lookup.
 */
// UI-01 hardening: `state` REQUIRED — see searchGymsByLocation's options note.
export const getGymFromDisplay = async (identifier: string, state: StateSlug): Promise<Gym | null> => {
    if (!identifier) return null;

    try {
        const docRef = doc(db, 'display', state, 'gyms', identifier);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                id: docSnap.id,
                ...data,
                // Standardize property names from Firestore 'display' collection to 'Gym' interface
                // ⚠️ ORDER FLIPPED (L13, operator-ruled 2026-08-20). MEASURED read-only against prod:
                // `styles` is present on ALL 2,750 display docs and `allStyles` on ZERO, so the
                // second operand used to be the ONLY live path — which made the line that keeps
                // every gym page working read as deletable dead code. Reading the real field FIRST
                // makes the live path the obvious one; `allStyles` is retained as a forward-compatible
                // alias and is now the genuinely-redundant half. Output key stays `allStyles` (the Gym
                // interface's name) — only the INPUT preference moved.
                allStyles: data.styles || data.allStyles || [],
                ageGroups: data.ageGroups || [],
                // ⚠️ ORDER FLIPPED (L13) — same measurement: `experienceLevel` (singular) on all 2,750,
                // `experienceLevels` on zero.
                experienceLevels: data.experienceLevel || data.experienceLevels || [],
                // H1/M1: register spelling, matching the prerendered page. Conditional
                // so an absent suburb stays absent rather than becoming ''.
                ...(data.suburb ? { suburb: canonicaliseSuburbName(data.suburb) } : {})
            } as Gym;
        }

        return null;
    } catch (error) {
        console.error("[locationService] getGymFromDisplay error:", safeErrorMessage(error));
        return null;
    }
};

/**
 * Fetch multiple gyms by ID from the display collection
 */
export const batchGetGymsFromDisplay = async (placeIds: string[], states: readonly StateSlug[] = LAUNCHED_STATES): Promise<Gym[]> => {
    if (!placeIds || placeIds.length === 0) return [];

    try {
        const BATCH_SIZE = 30; // Firestore documentId() 'in' limit
        const byId = new Map<string, Gym>();

        // R-02: favourites can span states, and stableIds are unique across the whole
        // corpus (cross-state overlap measured at 0), so the default is the union of
        // every launched state — a VIC favourite previously vanished because only
        // display/nsw/gyms was read. One `in` query per 30 IDs per state instead of N
        // individual getDocs — same read count, far fewer round-trips (Phase 1 item 17).
        // Mapping mirrors getGymFromDisplay so output is identical.
        for (const state of states) {
            const gymsRef = collection(db, 'display', state, 'gyms');
            for (let i = 0; i < placeIds.length; i += BATCH_SIZE) {
                const chunk = placeIds.slice(i, i + BATCH_SIZE);
                const snapshot = await getDocs(query(gymsRef, where(documentId(), 'in', chunk)));
                snapshot.forEach(docSnap => {
                    const data = docSnap.data();
                    byId.set(docSnap.id, {
                        id: docSnap.id,
                        ...data,
                        // ⚠️ ORDER FLIPPED (L13, operator-ruled 2026-08-20). MEASURED read-only against prod:
                // `styles` is present on ALL 2,750 display docs and `allStyles` on ZERO, so the
                // second operand used to be the ONLY live path — which made the line that keeps
                // every gym page working read as deletable dead code. Reading the real field FIRST
                // makes the live path the obvious one; `allStyles` is retained as a forward-compatible
                // alias and is now the genuinely-redundant half. Output key stays `allStyles` (the Gym
                // interface's name) — only the INPUT preference moved.
                allStyles: data.styles || data.allStyles || [],
                        ageGroups: data.ageGroups || [],
                        // ⚠️ ORDER FLIPPED (L13) — same measurement: `experienceLevel` (singular) on all 2,750,
                // `experienceLevels` on zero.
                experienceLevels: data.experienceLevel || data.experienceLevels || [],
                        // H1/M1 — mirrors getGymFromDisplay so output stays identical.
                        ...(data.suburb ? { suburb: canonicaliseSuburbName(data.suburb) } : {})
                    } as Gym);
                });
            }
        }

        // Preserve input order and drop misses (matches the previous behaviour).
        return placeIds.map(id => byId.get(id)).filter((g): g is Gym => g !== undefined);
    } catch (error) {
        console.error("[locationService] batchGetGymsFromDisplay error:", safeErrorMessage(error));
        return [];
    }
};

/**
 * Google Places API Utilities (proxied via Cloud Functions)
 */

/**
 * Reverse-resolve a lat/lng to the nearest locality of ANY LAUNCHED STATE via the
 * nearestLocation Cloud Function (GNAF-backed) — replaces the paid Google
 * reverse-geocode for the "use my location" feature (item 16). Returns null on
 * failure so callers can fall back gracefully.
 *
 * The function scans the union of every launched state's register, so this needs no
 * state argument and the caller does not have to know which state a coordinate is in —
 * the nearest entry across the union IS the answer. While one state is launched the
 * union is that state, so the result is unchanged.
 */
export const resolveNearestLocation = async (
    lat: number,
    lng: number
): Promise<{ display: string; entryId: string; postcode: string; lat: number; lng: number; stateSlug?: StateSlug } | null> => {
    try {
        const nearestLocationFn = httpsCallable(functions, 'nearestLocation');
        const result = await nearestLocationFn({ lat, lng });
        const data = result.data as { ok?: boolean; display?: string; entryId?: string; postcode?: string; lat?: number; lng?: number; stateSlug?: string };
        if (!data || data.ok === false || !data.display) return null;
        // UI-28: the function now returns the slug of the state the coordinate
        // resolved to. Optional on the wire — an older deployed function omits it —
        // and allowlisted against LAUNCHED_STATES so a junk value can never reach a
        // URL builder. Absent/invalid → the field is omitted and callers keep
        // today's DEFAULT_STATE behaviour (graceful until the functions deploy lands).
        const stateSlug = LAUNCHED_STATES.includes(data.stateSlug as StateSlug)
            ? (data.stateSlug as StateSlug)
            : undefined;
        return {
            display: data.display,
            entryId: data.entryId || '',
            postcode: data.postcode || '',
            lat: data.lat ?? lat,
            lng: data.lng ?? lng,
            ...(stateSlug ? { stateSlug } : {}),
        };
    } catch (error) {
        console.error("[locationService] resolveNearestLocation error:", safeErrorMessage(error));
        return null;
    }
};

/**
 * Get location suggestions (suburbs/postcodes) using the autocompleteLocation Cloud Function.
 */
// UI-01 hardening: `state` REQUIRED — see searchGymsByLocation's options note.
export const getLocationSuggestions = async (
    query: string,
    state: StateSlug,
    // UI-28 review hardening: an empty result from a THROWN transport error or a
    // server THROTTLED reply is otherwise indistinguishable from "no such suburb
    // in this state" — and in the both-states fan-out, a silently missing state
    // flips the hero's Enter rule from ambiguous→hold into one-state→navigate on
    // the 19 cross-state name collisions (the UI-01 wrong-state family). The
    // optional callback lets a fan-out caller learn WHICH state's block is
    // unreliable; single-state callers simply omit it and behave exactly as before.
    opts?: { onUnavailable?: (state: StateSlug) => void },
): Promise<LocationSelection[]> => {
    if (!query || query.trim().length < 2) {
        return [];
    }

    try {
        const autocompleteLocationFn = httpsCallable<{ query: string; state: StateSlug }, any>(functions, 'autocompleteLocation');
        // R-02: state omitted → NSW suggestions only; callers pass the page's state.
        const result = await autocompleteLocationFn({ query, state });
        const data = result.data;

        // Server-side throttle: an empty-but-not-authoritative answer. Signal it —
        // the entries genuinely may exist; this state's block just can't say.
        if (data.reason === "THROTTLED") {
            opts?.onUnavailable?.(state);
            return [];
        }

        // Structured error from backend
        if (data.ok === false && data.reason === "INDEX_UNAVAILABLE") {
            // Per-state contract (changelog (+11)): the marker is labelled with the
            // REQUESTED state — it used to hardcode DEFAULT_STATE_DISPLAY, which made
            // the failing state unrepresentable and forced consumers to fail closed
            // for every state at once. The marker channel also joins thrown/THROTTLED
            // on the onUnavailable signal, so all three failure legs reach the hero's
            // Enter-decline through one mechanism.
            opts?.onUnavailable?.(state);
            return [{
                state: stateSlugToDisplay(state),
                suburb: "",
                postcode: "",
                display: "INDEX_UNAVAILABLE_MARKER", // Marker for UI
                source: "index"
            } as any];
        }

        if (data.fullEntries) {
            return data.fullEntries.map((e: any) => ({
                // Absent state field resolves to the REQUESTED state (UI-28). The old
                // R2-rule-5 fallback to DEFAULT_STATE_DISPLAY predates the both-states
                // fan-out: on a `state: 'vic'` call it would label a state-less entry
                // NSW, and a mislabelled entry routes a URL to the wrong corpus.
                // Identical for DEFAULT_STATE calls (the only shape before fan-out).
                state: e.state || stateSlugToDisplay(state),
                suburb: e.locality,
                postcode: e.postcode,
                entryId: e.entryId,
                lat: e.lat,
                lng: e.lng,
                display: e.display,
                slug: e.slug,
                source: data.source
            }));
        }

        // Fallback for older legacy results (just strings)
        return (data.suggestions || []).map((s: string) => ({
            // UI-28: label with the REQUESTED state, not DEFAULT_STATE — see the
            // fullEntries note above. Identical for DEFAULT_STATE calls.
            state: stateSlugToDisplay(state),
            suburb: s.split(' ')[0], // Rough fallback
            postcode: s.split(' ').pop() || "",
            display: s
        }));
    } catch (error) {
        console.error("[locationService] autocompleteLocation error:", safeErrorMessage(error));
        opts?.onUnavailable?.(state);
        return [];
    }
};

/**
 * Resolve a location by its parts (state, suburb, postcode) using the resolveLocation Cloud Function.
 */
export const resolveLocation = async (params: { state: string; suburb: string; postcode: string }): Promise<LocationSelection | null> => {
    try {
        const resolveLocationFn = httpsCallable<any, any>(functions, 'resolveLocation');
        const result = await resolveLocationFn(params);
        const data = result.data;

        if (data.ok && data.entry) {
            const e = data.entry;
            return {
                state: e.state,
                suburb: e.locality,
                postcode: e.postcode,
                entryId: e.entryId,
                lat: e.lat,
                lng: e.lng,
                display: e.display,
                slug: e.slug,
                source: data.source
            };
        }

        if (data.ok === false && data.reason === "INDEX_UNAVAILABLE") {
            // Signal unavailability to caller, labelled with the state that was
            // actually asked for (item 7, operator-ruled 2026-08-14).
            //
            // This hardcoded DEFAULT_STATE_DISPLAY until now, which made the failing
            // state unrepresentable — the same defect its sibling at :283 carried and
            // had fixed (changelog (+11)). ⚠️ It was INERT rather than harmless: this
            // object sets `display: "INDEX_UNAVAILABLE"` while the only consumer tests
            // for `"INDEX_UNAVAILABLE_MARKER"`, so nothing ever read the wrong value.
            // Fixed while inert deliberately — the sibling was NOT inert when it had
            // this bug, and a third state launch is what would make this one reachable.
            // The two marker SHAPES are deliberately left different: unifying them
            // would wake a dormant consumer path, which is a behaviour change and not
            // what this item is for.
            return {
                state: stateSlugToDisplay(params.state),
                suburb: "",
                postcode: "",
                source: "index",
                display: "INDEX_UNAVAILABLE"
            } as any;
        }

        return null;
    } catch (error) {
        console.error("[locationService] resolveLocation error:", safeErrorMessage(error));
        return null;
    }
};
